import { baseClient } from 'src/app/baseClient';

import {
  BannerApiItem,
  BannerListParams,
  BannerListResponse,
  BannerOrderItem,
  BannerOrderParams,
  BannerOrderUpdatePayload,
} from '../types/bannerManagement';
import {
  MarketplaceCategory,
  MarketplaceItemStatus,
  MarketplaceLane,
  MarketplaceListParams,
  MarketplaceListResponse,
  MarketplaceOffersListResponse,
  MarketplaceOffersParams,
  MarketplaceOfferV2,
  MarketplacePayload,
  MarketplaceProduct,
  MarketplaceProductResponse,
  MarketplacePropertiesResponse,
} from '../types/types';
import {
  normalizeOffersResponse,
  removeUndefinedParams,
  serializeArrayParams,
} from '../utils/marketplaceOffersApi.utils';
import { BannerPayload } from '../utils/buildBannerPayload';

interface BannerResponse {
  id: number;
}

async function createProduct(
  payload: MarketplacePayload
): Promise<MarketplaceProductResponse> {
  const { data } = await baseClient.post<MarketplaceProduct & { id: number }>(
    '/marketplace/products',
    payload
  );
  return data;
}

async function createBanner(payload: BannerPayload): Promise<BannerResponse> {
  const { data } = await baseClient.post<BannerResponse>(
    '/marketplace/banners',
    payload
  );

  return data;
}

async function updateBanner(
  id: number,
  payload: BannerPayload
): Promise<BannerApiItem> {
  const { data } = await baseClient.put<BannerApiItem>(
    `/marketplace/banners/${id}`,
    payload
  );

  return data;
}

async function updateProduct(
  id: number,
  payload: Partial<MarketplacePayload>
): Promise<void> {
  await baseClient.put(`/marketplace/products/${id}?validate=true`, payload);
}

async function uploadProductImage(
  id: number,
  imageKey: string,
  file: File
): Promise<void> {
  const formData = new FormData();
  formData.append(imageKey, file);

  await baseClient.post(`/marketplace/products/${id}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

async function uploadBannerImage(
  id: number,
  imageKey: string,
  file: File
): Promise<void> {
  const formData = new FormData();
  formData.append(imageKey, file);

  await baseClient.post(`/marketplace/banners/${id}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

async function fetchBanners(
  params: BannerListParams
): Promise<BannerListResponse> {
  const query = new URLSearchParams({
    ...(params.page && { page: params.page.toString() }),
    ...(params.limit && { limit: params.limit.toString() }),
    ...(params.title && { title: params.title }),
    ...(params.status && { status: params.status }),
    ...(params.deviceType && { deviceType: params.deviceType }),
    ...(params.slotType && { slotType: params.slotType }),
    ...(params.targetMarket && { targetMarket: params.targetMarket }),
    ...(params.startDate && { createdAtFrom: params.startDate }),
    ...(params.endDate && { createdAtTo: params.endDate }),
  });

  const endpoint = `/marketplace/banners?${query.toString()}`;
  const { data } = await baseClient.get<BannerListResponse | BannerApiItem[]>(
    endpoint
  );

  if (Array.isArray(data)) {
    return {
      data,
      total: data.length,
    };
  }

  return data;
}

async function fetchBannerById(id: number): Promise<BannerApiItem> {
  const { data } = await baseClient.get<BannerApiItem>(
    `/marketplace/banners/${id}`
  );

  return data;
}

async function fetchBannerOrder(
  params: BannerOrderParams
): Promise<BannerOrderItem[]> {
  const query = new URLSearchParams({
    deviceType: params.deviceType,
    slotType: params.slotType,
    targetMarket: params.targetMarket,
  });

  const { data } = await baseClient.get<
    BannerOrderItem[] | { banners: BannerOrderItem[] }
  >(`/marketplace/banners/order?${query.toString()}`);

  if (Array.isArray(data)) {
    return data;
  }

  return data.banners ?? [];
}

async function updateBannerOrder(
  payload: BannerOrderUpdatePayload
): Promise<void> {
  await baseClient.put('/marketplace/banners/order', payload);
}

async function activateBanner(id: number): Promise<void> {
  await baseClient.patch(`/marketplace/banners/${id}/activate`);
}

async function deactivateBanner(id: number): Promise<void> {
  await baseClient.patch(`/marketplace/banners/${id}/deactivate`);
}

async function deleteBanner(id: number): Promise<void> {
  await baseClient.delete(`/marketplace/banners/${id}`);
}

async function fetchProducts(
  params: MarketplaceListParams
): Promise<MarketplaceListResponse> {
  const query = new URLSearchParams({
    type: params.type,
    ...(params.page && { page: params.page.toString() }),
    ...(params.limit && { limit: params.limit.toString() }),
    ...(params.title && { title: params.title }),
    ...(params.status && { status: params.status }),
    ...(params.startDate && { startDate: params.startDate }),
    ...(params.endDate && { endDate: params.endDate }),
  });

  const endpoint = `/marketplace/products?${query.toString()}`;

  const { data } = await baseClient.get<MarketplaceListResponse>(endpoint);
  return data;
}

async function fetchProductById(
  id: number
): Promise<MarketplaceProductResponse> {
  const { data } = await baseClient.get<MarketplaceProductResponse>(
    `/marketplace/products/${id}`
  );
  return data;
}

async function fetchProductsList(): Promise<{ id: number; title: string }[]> {
  const { data } = await baseClient.get<{ id: number; title: string }[]>(
    '/marketplace/products/list'
  );
  return data;
}

async function fetchCategories(): Promise<MarketplaceCategory[]> {
  const { data } = await baseClient.get<MarketplaceCategory[]>(
    '/marketplace/categories'
  );
  return data;
}

async function fetchLanes(): Promise<MarketplaceLane[]> {
  const { data } =
    await baseClient.get<MarketplaceLane[]>('/marketplace/lanes');
  return data;
}

async function fetchProperties(): Promise<MarketplacePropertiesResponse> {
  const { data } = await baseClient.get<MarketplacePropertiesResponse>(
    '/products/v2/properties'
  );
  return data;
}

async function fetchOffers(
  params: MarketplaceOffersParams
): Promise<MarketplaceOffersListResponse> {
  const normalizedParams = removeUndefinedParams(
    params as Record<string, unknown>
  );

  const { data } = await baseClient.get<
    | MarketplaceOfferV2[]
    | MarketplaceOffersListResponse
    | {
        data?: MarketplaceOfferV2[];
        total?: number;
        items?: MarketplaceOfferV2[];
      }
  >('/products/v2/offers', {
    params: normalizedParams,
    paramsSerializer: serializeArrayParams,
  });

  return normalizeOffersResponse(data);
}

async function updatePublishedProduct(
  id: number,
  payload: MarketplacePayload
): Promise<MarketplaceProductResponse> {
  const { data } = await baseClient.put<MarketplaceProductResponse>(
    `/marketplace/products/${id}/published`,
    payload
  );
  return data;
}

async function activateItem(id: number): Promise<void> {
  await baseClient.patch(`/marketplace/products/${id}/publish`, {
    status: MarketplaceItemStatus.Active,
  });
}

async function deactivateItem(id: number): Promise<void> {
  await baseClient.patch(`/marketplace/products/${id}/unpublish`, {
    status: MarketplaceItemStatus.Unpublished,
  });
}

async function cancelSchedule(id: number): Promise<void> {
  await baseClient.delete(`/marketplace/products/${id}/schedule`);
}

export const marketplaceApi = {
  fetchCategories,
  fetchLanes,
  fetchProperties,
  fetchOffers,
  createProduct,
  createBanner,
  updateBanner,
  updateProduct,
  uploadProductImage,
  uploadBannerImage,
  fetchProducts,
  fetchProductsList,
  fetchProductById,
  fetchBanners,
  fetchBannerById,
  fetchBannerOrder,
  updateBannerOrder,
  updatePublishedProduct,
  activateItem,
  deactivateItem,
  activateBanner,
  deactivateBanner,
  deleteBanner,
  cancelSchedule,
};
